'use client';

import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ComponentProps,
  type ReactNode,
} from 'react';
import Image from 'next/image';
import { Popover } from '@base-ui/react/popover';
import cn from 'classnames';
import { CheckIcon, XIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'next-i18next';

import usePrefersReducedMotion from '@hooks/usePrefersReducedMotion';
import { titleCase } from '@utils/plp/utils/transforms';
import type {
  ColourOption,
  SizeOption,
} from '@adapters/bigcommerce/hooks/product/variantAvailability';
import SelectionCell from '_components/UI/SelectionCell';
import SimpleLink from '_components/UI/SimpleLink';
import LoadingSpinner from '@components/ui/LoadingSpinner';

import QuickAddTrigger from '../QuickAddTrigger';
import QuickAddColourSwatch from './ColourSwatch';
import styles from './QuickAddPopover.module.scss';

/** CTA height — the desktop panel's bottom edge aligns with the trigger's. */
const TRIGGER_HEIGHT = 48;
/** Figma 801:83103 gap between the tile and the stacked card below it —
    mirrors `--component-spacing-8` (Base UI's `sideOffset` takes a plain
    number, not a CSS var, same reason `TRIGGER_HEIGHT` is hardcoded). */
const STACKED_GAP = 8;
const SKELETON_CELL_COUNT = 8;
/** The `'chip'` card sits over its anchor rather than below it (Figma
    867:192082 step 1), so its top edge lines up with the anchor's. A
    function so it follows the anchor's real height. */
const overlayAnchorOffset = ({ anchor }: { anchor: { height: number } }) =>
  -anchor.height;

/**
 * Compound Quick Add popover (Figma "Add to cart Popover", node 372:4915;
 * desktop uses the Application=Product Card variants, e.g. 746:239031).
 *
 * `Root`/`Trigger`/`Content` wrap the Base UI popover machinery across three
 * layouts:
 * - `'sheet'`: viewport-bottom sheet with backdrop (narrow viewports).
 * - `'desktop'`: panel anchored over the card image bottom (PLP grid card).
 * - `'stacked'`: a rounded card anchored directly below the trigger's host,
 *   growing in place rather than overlaying an image (Figma 801:83103,
 *   `ImageHotspot/ProductTile`'s `compact` quick add — that host is its own
 *   56px slot, not an image, so there's nothing to overlay).
 *   `stackedVariant="chip"` is `WhyChoose`'s version (Figma 867:193013): a
 *   fixed 405px card laid over its thumbnail+cart chip, right edges aligned,
 *   with a `ProductSummary` above the grid.
 *
 * The body parts — `Header`, `SizeGrid`, `Skeleton`, `Spinner`, `Success`,
 * `Fallback` — render standalone in Storybook/specs: layout-sensitive parts
 * read `QuickAddPopoverContext` when inside `Root` and default to the
 * desktop layout otherwise. They compose existing UI subcomponents
 * (`SelectionCell`, `SimpleLink`, `TextButton`/`IconButton` via the
 * trigger).
 *
 * ```tsx
 * <QuickAddPopover.Root open={open} onOpenChange={setOpen} layout={layout}>
 *   <QuickAddPopover.Trigger label="Quick Add to Cart" aria-label="…" />
 *   <QuickAddPopover.Content aria-labelledby={titleId}>
 *     <QuickAddPopover.Header titleId={titleId} onClose={close} />
 *     <QuickAddPopover.SizeGrid sizes={sizes} onSizeSelect={add} />
 *   </QuickAddPopover.Content>
 * </QuickAddPopover.Root>
 * ```
 */

export type QuickAddPopoverLayout = 'sheet' | 'desktop' | 'stacked';

/** Which host the `'stacked'` layout is for — see the component doc. */
export type QuickAddStackedVariant = 'tile' | 'chip';

export type QuickAddPopoverContextValue = {
  layout: QuickAddPopoverLayout;
  stackedVariant?: QuickAddStackedVariant;
};

/** Exported so stories/specs can force a layout without mounting `Root`. */
export const QuickAddPopoverContext =
  createContext<QuickAddPopoverContextValue | null>(null);

function useQuickAddPopoverContext() {
  const ctx = useContext(QuickAddPopoverContext);
  if (!ctx) {
    throw new Error(
      'QuickAddPopover components must be used within QuickAddPopover.Root'
    );
  }
  return ctx;
}

/** Body-part layout: reads `QuickAddPopoverContext` when inside a mounted
    `Root`, defaulting to `'desktop'` for standalone renders (stories/specs). */
function useQuickAddPopoverLayout(): QuickAddPopoverLayout {
  return useContext(QuickAddPopoverContext)?.layout ?? 'desktop';
}

/** True for the `'stacked'` layout's `'chip'` variant (`WhyChoose`). */
function useIsChipLayout(): boolean {
  const ctx = useContext(QuickAddPopoverContext);
  return ctx?.layout === 'stacked' && ctx.stackedVariant === 'chip';
}

export type QuickAddPopoverRootProps = {
  children: ReactNode;
  open?: boolean;
  /** Base UI's own `onOpenChange` — receives `(open, eventDetails)` so the
      caller can inspect `eventDetails.reason` and `.cancel()` unwanted
      dismissals (e.g. an outside press while an add is in flight). */
  onOpenChange?: ComponentProps<typeof Popover.Root>['onOpenChange'];
  /** Owned by the caller (`_components/QuickAdd/index.tsx`) rather than
      computed here — the orchestrator already needs this same breakpoint
      for its own anchor/pointer-leave/scroll-lock logic, and every card on
      a PLP grid mounts a `Root`, so a second independent `useBreakpoint`
      call here would double up on resize listeners across the grid and
      risked drifting out of sync with the caller's own value. */
  layout: QuickAddPopoverLayout;
  /** Only read by the `'stacked'` layout — see `QuickAddStackedVariant`. */
  stackedVariant?: QuickAddStackedVariant;
};

function QuickAddPopoverRoot({
  children,
  open,
  onOpenChange,
  layout,
  stackedVariant = 'tile',
}: QuickAddPopoverRootProps) {
  const contextValue = useMemo(
    () => ({ layout, stackedVariant }),
    [layout, stackedVariant]
  );
  return (
    <QuickAddPopoverContext.Provider value={contextValue}>
      <Popover.Root
        open={open}
        onOpenChange={onOpenChange}
        modal={layout === 'sheet'}
      >
        {children}
      </Popover.Root>
    </QuickAddPopoverContext.Provider>
  );
}

export type QuickAddPopoverTriggerProps = {
  /** Visible CTA label (desktop face). */
  label: string;
  'aria-label': string;
  /**
   * Exposes the trigger's DOM node to the caller — Base UI restores focus
   * to it on Escape/outside-press, but a close driven from outside that
   * machinery (e.g. a `setTimeout`, with no originating DOM event for Base
   * UI to key off of) doesn't get that for free, so the caller focuses it
   * explicitly in those cases.
   */
  triggerRef?: React.Ref<HTMLButtonElement>;
  /** Forwarded to `QuickAddTrigger`'s mobile/icon face — see its own doc. */
  iconVariant?: 'overlay' | 'inline' | 'withImage';
  /** Forwarded to `QuickAddTrigger` — see its own doc. */
  pending?: boolean;
};

function QuickAddPopoverTrigger({
  label,
  'aria-label': ariaLabel,
  triggerRef,
  iconVariant,
  pending,
}: QuickAddPopoverTriggerProps) {
  const { layout } = useQuickAddPopoverContext();
  // The stacked layout (ImageHotspot's compact tile) still wants the
  // always-visible icon face, not the hover-revealed desktop CTA — only the
  // true desktop layout (PLP grid) gets that face.
  const isIconFace = layout !== 'desktop';

  return (
    <Popover.Trigger
      render={(triggerProps) => {
        // Strip Base UI's native `type` so it can't collide with the
        // TextButton/IconButton visual `type` prop.
        const { type: _type, ...rest } =
          triggerProps as React.ButtonHTMLAttributes<HTMLButtonElement>;
        return (
          <QuickAddTrigger
            {...rest}
            ref={triggerRef}
            isMobileLayout={isIconFace}
            iconVariant={iconVariant}
            pending={pending}
            label={label}
            aria-label={ariaLabel}
          />
        );
      }}
    />
  );
}

export type QuickAddPopoverContentProps = {
  children: ReactNode;
  /**
   * Each value is its own `AnimatePresence` key (see the `motion.div`
   * below) — switching between them slides the previous surface off and
   * the new one in, reusing whichever direction `offscreenY` resolves to
   * for the current layout. `'success'` also collapses the panel to the
   * 56px confirmation bar; `'panel'`, `'spinner'`, and `'fallback'` all get
   * the same full panel chrome (`surfacePanel`), just different children —
   * `QuickAdd/index.tsx`'s `contentVariant` decides which of the four is
   * showing at any moment (see its own doc for why skeleton and the size
   * grid deliberately share `'panel'` rather than getting their own keys).
   */
  variant?: 'panel' | 'spinner' | 'fallback' | 'success';
  'aria-labelledby'?: string;
  /** Desktop: bounds `size()`'s available space to the card image instead
      of the viewport — drives `--available-width` (`.positionerDesktop`)
      and is the pre-measurement fallback for `availableHeight` below. */
  collisionBoundary?: Element | null;
  /** Desktop: the measured room above the anchor within the card, exposed
      as `--card-available-height` for the panel's max-height cap (Figma's
      400px limit) — a static value, unlike floating-ui's viewport-relative
      `--available-height`, so it doesn't shift as the page scrolls. */
  availableHeight?: number | null;
  /** Desktop: a stable positioning reference, separate from the actual
      trigger button — the trigger animates itself out of view once open,
      and Base UI would otherwise track that shifting rect and drag the
      panel down with it. Undefined on mobile (fixed sheet, no anchoring). */
  anchor?: React.RefObject<Element | null>;
  /** Popover open state. Desktop only: when it flips to `false` the surface
      slides down (Figma step 6). Mobile slides the whole sheet via Base UI. */
  open?: boolean;
};

function QuickAddPopoverContent({
  children,
  variant = 'panel',
  'aria-labelledby': ariaLabelledBy,
  collisionBoundary,
  anchor,
  availableHeight,
  open,
}: QuickAddPopoverContentProps) {
  const { layout } = useQuickAddPopoverContext();
  const isSheet = layout === 'sheet';
  const isStacked = layout === 'stacked';
  const isDesktopPanel = layout === 'desktop';
  const isChip = useIsChipLayout();
  const prefersReducedMotion = usePrefersReducedMotion();

  // Sheet close: slides the whole sheet via Base UI, so it never drives the
  // surface here. Desktop and stacked both close by sliding the surface
  // off (see the motion.div below).
  const isClosing = open === false && !isSheet;

  // Desktop's panel/success surfaces slide DOWN off the bottom edge — it
  // overlays the card's image, so retreating "through" that image's bottom
  // edge (Content's own popup `overflow: clip`) reads correctly. The
  // stacked card (Figma 867:163554, ImageHotspot's compact tile) has no
  // image edge below it to disappear into: its own Figma spec instead has
  // it emerge "from the back of" the tile and retreat back up behind it —
  // so it slides the opposite way, UP off the top edge.
  const offscreenY = isStacked ? '-100%' : '100%';

  // The mobile confirmation bar (Figma 801:82290) is a different shape from
  // the flush size sheet: it floats inset from the viewport edges with its
  // own drop shadow, and the backdrop fades away behind it. Stacked's
  // success view stays in the same card position as its size selector
  // (Figma 801:83103) rather than becoming a floating toast.
  const isMobileSuccess = isSheet && variant === 'success';

  const surfaceClassName = cn(
    styles.surface,
    isSheet
      ? styles.surfaceSheet
      : isStacked
      ? styles.surfaceStacked
      : styles.surfaceDesktop,
    isChip && styles.surfaceChip,
    variant === 'success' ? styles.surfaceSuccess : styles.surfacePanel
  );

  // Mobile: pin the sheet/toast to the viewport bottom via INLINE styles, not
  // just the `.positionerSheet` / `.positionerSheetSuccess` classes. On mobile
  // we hand Base UI no anchor, so floating-ui falls back to anchoring the popup
  // over the card's trigger button — mid-viewport. The class-based `!important`
  // overrides are what normally pull it to the bottom, but when that stylesheet
  // is momentarily out of sync with the DOM (a hashed CSS-module class in
  // cached JS/HTML that no longer matches the served CSS chunk — seen on the
  // frequently-redeployed preview), the override silently drops and the sheet
  // strands at floating-ui's mid-screen anchor. Inline styles ship in the JS
  // bundle alongside this JSX so they can't fall out of sync, and Base UI
  // merges the caller `style` *after* floating-ui's own inline positioning, so
  // these win over it without needing `!important`. The classes stay as a
  // matched belt-and-suspenders (their `!important` values mirror these); if
  // the CSS does load it wins with the identical result. `dvh`/`env()` are
  // expressed as strings since React inline styles can't carry units otherwise.
  const positionerStyle: React.CSSProperties = isDesktopPanel
    ? availableHeight != null
      ? ({
          '--card-available-height': `${availableHeight}px`,
        } as React.CSSProperties)
      : {}
    : isStacked
    ? {}
    : isMobileSuccess
    ? {
        position: 'fixed',
        top: 'auto',
        transform: 'none',
        opacity: 1,
        // Mirrors `.positionerSheetSuccess`: floats inset from the edges (16px,
        // or the safe-area inset when deeper) as a toast once the backdrop goes.
        bottom: 'max(var(--component-spacing-16), env(safe-area-inset-bottom))',
        left: 'max(var(--component-spacing-16), env(safe-area-inset-left))',
        right: 'max(var(--component-spacing-16), env(safe-area-inset-right))',
        width: 'auto',
        // Figma (801:82290) caps the toast at 315px and centres it, rather than
        // stretching edge-to-edge like the size sheet. `margin-inline: auto`
        // centres it within the left/right insets; on phones narrower than
        // 315px + insets it shrinks to fit instead of overflowing.
        maxWidth: '315px',
        marginInline: 'auto',
      }
    : {
        // Mirrors `.positionerSheet`: the flush, edge-to-edge size sheet.
        position: 'fixed',
        top: 'auto',
        bottom: 0,
        left: 0,
        right: 0,
        transform: 'none',
        width: '100%',
        maxWidth: 'none',
        opacity: 1,
      };

  return (
    <Popover.Portal>
      {isSheet && (
        <Popover.Backdrop
          className={cn(
            styles.backdrop,
            isMobileSuccess && styles.backdropHidden
          )}
        />
      )}
      <Popover.Positioner
        side={isStacked ? 'bottom' : 'top'}
        align={isChip ? 'end' : 'center'}
        anchor={isSheet ? undefined : anchor}
        sideOffset={
          isSheet
            ? 0
            : isChip
            ? overlayAnchorOffset
            : isStacked
            ? STACKED_GAP
            : -TRIGGER_HEIGHT
        }
        collisionBoundary={
          isDesktopPanel ? collisionBoundary ?? undefined : undefined
        }
        // Desktop: the panel must stay pinned over the card's image, never
        // flip to the opposite side or shift along the trigger's axis as
        // scroll changes the room above it (Figma has no "flipped"
        // variant). It still shrinks via `--card-available-height` below,
        // with `.surfacePanel`'s internal scroll handling overflow. Stacked
        // has no image to stay pinned over, so it keeps Base UI's default
        // flip/shift collision avoidance instead — it just needs to stay
        // on-screen below the tile.
        collisionAvoidance={
          isDesktopPanel ? { side: 'none', align: 'none' } : undefined
        }
        className={cn(
          styles.positioner,
          isSheet
            ? styles.positionerSheet
            : isChip
            ? styles.positionerChip
            : isStacked
            ? styles.positionerStacked
            : styles.positionerDesktop,
          isMobileSuccess && styles.positionerSheetSuccess
        )}
        style={positionerStyle}
      >
        <Popover.Popup
          aria-labelledby={ariaLabelledBy}
          className={cn(
            styles.popup,
            isSheet
              ? styles.popupSheet
              : isStacked
              ? styles.popupStacked
              : styles.popupDesktop,
            isChip && styles.popupChip,
            isMobileSuccess && styles.popupSheetSuccess
          )}
        >
          {/* Panel → success (desktop: Figma step 4 of 372:4915; stacked:
              Figma step 4 of 867:163554): the outgoing surface slides off
              (`offscreenY`) through the popup's clipped edge while the new
              one slides in from that same edge. Spring 80/20 ≈ duration-
              bound bounce-0 spring over 400ms.

              Close (desktop: step 6 of 372:4915; stacked: step 6 of
              867:163554): the same surface slides off by animating `y` to
              `offscreenY` — clipped by the popup's `overflow: clip`, so it
              disappears at that edge rather than overshooting like a
              transform on the (portaled, unclipped) popup would. The child
              stays mounted so its grid cell never collapses — a removal would
              make Base UI's positioner re-measure and re-anchor. The popup
              itself just fades its drop-shadow out (SCSS `[data-ending-style]`),
              staying mounted long enough for this slide. Mobile keeps its Base
              UI sheet slide (`isClosing` is false there). */}
          {prefersReducedMotion ? (
            <div className={surfaceClassName}>{children}</div>
          ) : (
            <AnimatePresence initial={false}>
              <motion.div
                key={variant}
                className={surfaceClassName}
                initial={{ y: offscreenY }}
                animate={{ y: isClosing ? offscreenY : 0 }}
                exit={{ y: offscreenY }}
                transition={{ type: 'spring', duration: 0.4, bounce: 0 }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          )}
        </Popover.Popup>
      </Popover.Positioner>
    </Popover.Portal>
  );
}

export type QuickAddPopoverHeaderProps = {
  /** id for the "Select Size" heading (popup `aria-labelledby` target). */
  titleId?: string;
  onClose: () => void;
  /** Overrides the default "Select Size" title. */
  children?: ReactNode;
  /**
   * Keeps the title as the popup's accessible name without showing it —
   * for views (e.g. the spinner) where there's nothing left to select.
   */
  visuallyHidden?: boolean;
};

/**
 * Desktop (Figma 746:239031): centered "Select Size" (14/18 medium), no
 * close affordance — outside click/Esc dismisses. Sheet (Figma 801:81760)
 * and stacked (Figma 801:83103, `ImageHotspot`'s compact tile): left-aligned
 * title + 24px X close — stacked keeps that close affordance rather than
 * the Figma reference's "+" glyph, whose purpose the design's own
 * "further consideration" annotation leaves unresolved.
 */
function QuickAddPopoverHeader({
  titleId,
  onClose,
  children,
  visuallyHidden = false,
}: QuickAddPopoverHeaderProps) {
  const { t } = useTranslation('quickAdd');
  const layout = useQuickAddPopoverLayout();
  const isChip = useIsChipLayout();
  const hasCloseButton = layout !== 'desktop';

  return (
    <div
      className={cn(
        styles.header,
        hasCloseButton ? styles.headerSheet : styles.headerDesktop
      )}
    >
      <span
        id={titleId}
        className={cn(
          layout === 'sheet' || isChip
            ? styles.titleSheet
            : layout === 'stacked'
            ? styles.titleStacked
            : styles.titleDesktop,
          visuallyHidden && styles.titleHidden
        )}
      >
        {children ?? t('quickAdd.selectSize')}
      </span>
      {hasCloseButton ? (
        <button
          type="button"
          className={styles.closeButton}
          aria-label={t('quickAdd.close')}
          onClick={onClose}
        >
          <XIcon size={24} aria-hidden weight="regular" />
        </button>
      ) : null}
    </div>
  );
}

export type QuickAddPopoverColourRowProps = {
  colours: ColourOption[];
  selectedColourId?: number | null;
  onColourSelect?: (colour: ColourOption) => void;
};

/** Shared by `ColourRow` so its wrapper's per-layout gap/sizing lines up the
    same way `SizeGrid`'s does. */
function colourRowClassName(layout: QuickAddPopoverLayout) {
  return cn(
    styles.colourRow,
    layout === 'sheet'
      ? styles.colourRowSheet
      : layout === 'stacked'
      ? styles.colourRowStacked
      : styles.colourRowDesktop
  );
}

/**
 * Colour swatch row (ED-355), rendered above `SizeGrid` when the product has
 * more than one colour. Full ARIA radiogroup/radio pattern (matching the
 * PDP's own colour picker) rather than `SizeGrid`'s plain-button + `aria-pressed`
 * — colour swatches already carry that semantic on the PDP, so there's no
 * reason for Quick Add to diverge from it. Also mirrors the PDP's label row
 * (a static "Colour:" plus the selected colour's name) above the swatches —
 * with no selection yet (fresh open, before the initial-colour fallback has
 * landed) the name is simply omitted rather than shown blank.
 */
function QuickAddPopoverColourRow({
  colours,
  selectedColourId = null,
  onColourSelect,
}: QuickAddPopoverColourRowProps) {
  const { t } = useTranslation('quickAdd');
  const layout = useQuickAddPopoverLayout();
  const selectedColour = colours.find((c) => c.id === selectedColourId);

  return (
    <div className={styles.colourSection}>
      <div className={styles.colourLabelRow}>
        <span className={styles.colourLabel}>{t('quickAdd.colour')}</span>
        {selectedColour ? (
          <span
            className={styles.colourLabelName}
            data-testid="quick-add-selected-colour-name"
          >
            {titleCase(selectedColour.name)}
          </span>
        ) : null}
      </div>
      <div
        role="radiogroup"
        aria-label={t('quickAdd.selectColour')}
        className={colourRowClassName(layout)}
      >
        {colours.map((colour) => (
          <QuickAddColourSwatch
            key={colour.id}
            colour={colour}
            isSelected={colour.id === selectedColourId}
            onSelect={() => onColourSelect?.(colour)}
          />
        ))}
      </div>
    </div>
  );
}

export type QuickAddPopoverSizeGridProps = {
  sizes: SizeOption[];
  selectedSizeId?: number | null;
  onSizeSelect?: (size: SizeOption) => void;
};

const GRID_ARROW_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
]);

/** Shared by `SizeGrid` and `Skeleton` so their placeholder cells always
    line up with the real grid's layout. The `'chip'` card uses the sheet's
    grid: Figma 867:193013 has the same four equal 40px neutrals-100 cells
    as 801:81760, not the hotspot tile's 85px pills. */
function sizeGridClassName(layout: QuickAddPopoverLayout, isChip: boolean) {
  return cn(
    styles.sizeGrid,
    layout === 'sheet' || isChip
      ? styles.sizeGridSheet
      : layout === 'stacked'
      ? styles.sizeGridStacked
      : styles.sizeGridDesktop
  );
}

/**
 * Desktop (Figma 746:239031): equal-width cells in wrapping rows.
 * Sheet (Figma 801:81760): four equal columns. Stacked (Figma 801:83103,
 * `ImageHotspot`'s compact tile): fixed-width 85px cells in a wrapping,
 * space-between row. In all three, a partial last row keeps the cell width
 * instead of stretching to fill.
 */
function QuickAddPopoverSizeGrid({
  sizes,
  selectedSizeId = null,
  onSizeSelect,
}: QuickAddPopoverSizeGridProps) {
  const layout = useQuickAddPopoverLayout();
  const isChip = useIsChipLayout();
  const gridRef = useRef<HTMLDivElement>(null);

  // Desktop's column count is dynamic (`auto-fill`), so true 2D row/column
  // math isn't reliable — Left/Up and Right/Down instead step through the
  // enabled pills in visual (DOM) order, wrapping at the ends. This still
  // satisfies "arrow keys move between pills" without hardcoding a column
  // count that would drift from the actual rendered layout.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!GRID_ARROW_KEYS.has(event.key) || !gridRef.current) {
      return;
    }

    const cells = Array.from(
      gridRef.current.querySelectorAll<HTMLButtonElement>(
        'button:not(:disabled)'
      )
    );
    const currentIndex = cells.indexOf(
      document.activeElement as HTMLButtonElement
    );
    if (currentIndex === -1) {
      return;
    }

    event.preventDefault();

    const isForward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const nextIndex =
      (currentIndex + (isForward ? 1 : -1) + cells.length) % cells.length;
    cells[nextIndex]?.focus();
  };

  return (
    <div
      ref={gridRef}
      onKeyDown={handleKeyDown}
      className={sizeGridClassName(layout, isChip)}
    >
      {sizes
        .filter((size) => size.existsForSelectedColour)
        .map((size) => (
          <SelectionCell
            key={size.id}
            selected={size.id === selectedSizeId}
            disabled={
              !size.isAvailableForSelectedColour || !size.isPurchasableOnline
            }
            onClick={() => onSizeSelect?.(size)}
          >
            {size.name}
          </SelectionCell>
        ))}
    </div>
  );
}

export type QuickAddPopoverSkeletonProps = {
  /** Number of placeholder cells; defaults to {@link SKELETON_CELL_COUNT}
      when the caller has no variant-count hint. */
  cellCount?: number;
};

function QuickAddPopoverSkeleton({ cellCount }: QuickAddPopoverSkeletonProps) {
  const layout = useQuickAddPopoverLayout();
  const isChip = useIsChipLayout();
  const count = cellCount && cellCount > 0 ? cellCount : SKELETON_CELL_COUNT;

  return (
    <div
      className={sizeGridClassName(layout, isChip)}
      aria-busy="true"
      aria-hidden
    >
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className={styles.skeletonCell} />
      ))}
    </div>
  );
}

/** Centered spinner shown while a no-selection-needed add is in flight. */
function QuickAddPopoverSpinner() {
  return (
    <div className={styles.spinnerWrapper} aria-busy="true">
      <LoadingSpinner colour="var(--neutrals-1000)" />
    </div>
  );
}

export type QuickAddPopoverSuccessProps = {
  onClose: () => void;
  /** id for the "Added to Cart" label — the popup's `aria-labelledby`
      target while this view is showing (the `Header` title isn't rendered
      in this view, so the dialog would otherwise have no accessible name). */
  labelId?: string;
};

/** Green check + "Added to Cart" + X — the 56px confirmation bar. */
function QuickAddPopoverSuccess({
  onClose,
  labelId,
}: QuickAddPopoverSuccessProps) {
  const { t } = useTranslation('quickAdd');
  const layout = useQuickAddPopoverLayout();
  const isChip = useIsChipLayout();

  return (
    <div className={styles.success} role="status" aria-live="assertive">
      <CheckIcon
        size={24}
        weight="regular"
        aria-hidden
        className={styles.successIcon}
      />
      <span
        id={labelId}
        className={cn(
          styles.successLabel,
          isChip
            ? styles.successLabelChip
            : layout === 'stacked' && styles.successLabelStacked
        )}
      >
        {t('quickAdd.added')}
      </span>
      <button
        type="button"
        className={styles.closeButton}
        aria-label={t('quickAdd.close')}
        onClick={onClose}
      >
        <XIcon size={24} aria-hidden weight="regular" />
      </button>
    </div>
  );
}

export type QuickAddPopoverFallbackProps = {
  /** Message shown above the "View product" link. */
  message?: 'oos' | 'error' | 'callToPurchase' | 'insufficientStock' | null;
  /**
   * Product-specific copy for `callToPurchase` — the same
   * `shippingRestrictions.message_en`/`message_fr` HTML the PDP renders for
   * a call-to-order-only item. Falls back to a generic message when the
   * product has no configured restriction copy.
   */
  restrictionMessage?: string | null;
  /** Requested/available counts for `insufficientStock` — interpolated into
      the same copy the cart page shows for a stock shortfall. */
  messageVariables?: { attemptedToAdd: number; added: number } | null;
  productUrl: string;
};

/** Terminal fallback: optional message + "View product" link to the PDP. */
function QuickAddPopoverFallback({
  message = null,
  restrictionMessage = null,
  messageVariables = null,
  productUrl,
}: QuickAddPopoverFallbackProps) {
  const { t } = useTranslation('quickAdd');

  return (
    <div className={styles.fallback}>
      {message === 'callToPurchase' && restrictionMessage ? (
        <p
          className={styles.errorMessage}
          role="alert"
          dangerouslySetInnerHTML={{ __html: restrictionMessage }}
        />
      ) : message ? (
        <p className={styles.errorMessage} role="alert">
          {message === 'oos'
            ? t('quickAdd.oos')
            : message === 'callToPurchase'
            ? t('quickAdd.callToPurchase')
            : message === 'insufficientStock' && messageVariables
            ? // Mirrors the cart page's own copy for a stock shortfall
              // (`common:cart.message.insufficientQuantity`) rather than a
              // separate `plp`-namespace string, so Quick Add never
              // contradicts the message the shopper sees in-cart.
              t('common:cart.message.insufficientQuantity', messageVariables)
            : t('quickAdd.error.generic')}
        </p>
      ) : null}
      <SimpleLink
        href={productUrl}
        variant="secondary"
        size="small"
        className={styles.viewProductLink}
      >
        {t('quickAdd.viewProduct')}
      </SimpleLink>
    </div>
  );
}

export type QuickAddPopoverProductSummaryProps = {
  name: string;
  imageSrc: string;
  /** Formatted price; struck through beside `salePrice` when both are set. */
  displayPrice: string;
  salePrice?: string;
};

/**
 * The popover's own Mini Product Card (Figma 372:6981, inside 867:193013):
 * 80px image, name and price above the size grid — for a host whose trigger
 * doesn't already show them (`WhyChoose`'s thumbnail chip). Same type and
 * price treatment as `UI/Cards/MiniProductCard`, minus the link and cart
 * action, since it lives inside the add-to-cart flow itself.
 */
function QuickAddPopoverProductSummary({
  name,
  imageSrc,
  displayPrice,
  salePrice,
}: QuickAddPopoverProductSummaryProps) {
  return (
    <div className={styles.summary}>
      <div className={styles.summaryImage}>
        <Image
          src={imageSrc}
          alt=""
          fill
          sizes="80px"
          className={styles.summaryImg}
        />
      </div>
      <div className={styles.summaryInfo}>
        <p className={styles.summaryName}>{name}</p>
        <p className={styles.summaryPrice}>
          {salePrice ? (
            <>
              <span>{salePrice}</span>
              <span className={styles.summaryOriginalPrice}>
                {displayPrice}
              </span>
            </>
          ) : (
            <span>{displayPrice}</span>
          )}
        </p>
      </div>
    </div>
  );
}

const QuickAddPopover = {
  Root: QuickAddPopoverRoot,
  Trigger: QuickAddPopoverTrigger,
  Content: QuickAddPopoverContent,
  Header: QuickAddPopoverHeader,
  ProductSummary: QuickAddPopoverProductSummary,
  ColourRow: QuickAddPopoverColourRow,
  SizeGrid: QuickAddPopoverSizeGrid,
  Skeleton: QuickAddPopoverSkeleton,
  Spinner: QuickAddPopoverSpinner,
  Success: QuickAddPopoverSuccess,
  Fallback: QuickAddPopoverFallback,
} as const;

export default QuickAddPopover;

export {
  QuickAddPopoverRoot,
  QuickAddPopoverTrigger,
  QuickAddPopoverContent,
  QuickAddPopoverHeader,
  QuickAddPopoverProductSummary,
  QuickAddPopoverColourRow,
  QuickAddPopoverSizeGrid,
  QuickAddPopoverSkeleton,
  QuickAddPopoverSpinner,
  QuickAddPopoverSuccess,
  QuickAddPopoverFallback,
};
